import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  Package,
  Video,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  CheckCircle,
  Target,
  Loader2,
} from 'lucide-react';
import api from '../lib/api';

interface Stats {
  name: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  target?: string;
  progress?: number;
}

interface Order {
  id: string;
  market: string;
  status: string;
  total: string;
  time: string;
}

interface Alert {
  id: string | number;
  type: 'warning' | 'info' | 'success';
  message: string;
  market: string;
}

export default function Dashboard() {
  // Fetch ROAS Analytics
  const { data: roasData, isLoading: isRoasLoading, isError: isRoasError } = useQuery({
    queryKey: ['analytics', 'roas'],
    queryFn: async () => {
      const response = await api.get('/analytics/roas?period=30d');
      return response.data;
    },
    retry: 1,
  });

  // Fetch Recent Orders
  const { data: recentOrders, isLoading: isOrdersLoading, isError: isOrdersError } = useQuery({
    queryKey: ['orders', 'recent'],
    queryFn: async () => {
      const response = await api.get<Order[]>('/orders?limit=5');
      return response.data;
    },
    retry: 1,
  });

  // Fetch Alerts
  const { data: alerts, isLoading: isAlertsLoading, isError: isAlertsError } = useQuery({
    queryKey: ['alerts'],
    queryFn: async () => {
      const response = await api.get<Alert[]>('/alerts');
      return response.data;
    },
    retry: 1,
  });

  const stats: Stats[] = [
    { 
      name: 'GMV (This Month)', 
      value: roasData?.summary?.total_revenue_eur ? `€${roasData.summary.total_revenue_eur.toLocaleString()}` : '€0', 
      change: '+12.5%', 
      trend: 'up',
      target: '€50,000',
      progress: roasData?.summary?.total_revenue_eur ? Math.min(Math.round((roasData.summary.total_revenue_eur / 50000) * 100), 100) : 0,
    },
    { 
      name: 'Orders', 
      value: roasData?.summary?.orders?.toString() || '0', 
      change: '+8.2%', 
      trend: 'up',
    },
    { 
      name: 'Videos Generated', 
      value: '47', // Mocked as it might need another endpoint
      change: '+23%', 
      trend: 'up',
    },
    { 
      name: 'ROAS', 
      value: roasData?.summary?.roas ? `${roasData.summary.roas}x` : '0x', 
      change: '-2.1%', 
      trend: 'down',
    },
  ];

  const isLoading = isRoasLoading || isOrdersLoading || isAlertsLoading;
  const isError = isRoasError || isOrdersError || isAlertsError;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4 py-20">
        <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
        <p className="text-gray-400">Loading dashboard data...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4 py-20">
        <AlertTriangle className="h-12 w-12 text-red-500" />
        <p className="text-white font-semibold">Error loading dashboard data</p>
        <p className="text-gray-400 text-sm text-center max-w-md">
          The backend service might be unavailable or returning an invalid response.
          Please check your connection and try again.
        </p>
        <button 
          onClick={() => window.location.reload()}
          className="btn-primary mt-4"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400 mt-1">AutoPilot Commerce Agent v2 - Overview</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div key={stat.name} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">{stat.name}</p>
                <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
              </div>
              <div className={`
                flex items-center text-sm font-medium
                ${stat.trend === 'up' ? 'text-green-500' : 'text-red-500'}
              `}>
                {stat.trend === 'up' ? (
                  <ArrowUpRight className="h-4 w-4 mr-1" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 mr-1" />
                )}
                {stat.change}
              </div>
            </div>
            {stat.progress !== undefined && (
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">Progress to target ({stat.target})</span>
                  <span className="text-gray-300">{stat.progress}%</span>
                </div>
                <div className="h-2 bg-dark-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary-600 rounded-full"
                    style={{ width: `${stat.progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent orders */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Recent Orders</h2>
            <Link to="/orders" className="text-primary-400 hover:text-primary-300 text-sm">
              View all →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-400 text-sm border-b border-dark-700">
                  <th className="pb-3 font-medium">Order ID</th>
                  <th className="pb-3 font-medium">Market</th>
                  <th className="pb-3 font-medium">Status</th>
                  <th className="pb-3 font-medium">Total</th>
                  <th className="pb-3 font-medium">Time</th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
              {Array.isArray(recentOrders) && recentOrders.length > 0 ? (
                recentOrders.map((order) => (
                  <tr key={order.id || order.order_id} className="border-b border-dark-800">
                    <td className="py-3 font-medium text-white">{order.id || order.order_id}</td>
                    <td className="py-3">{order.market}</td>
                    <td className="py-3">
                      <span className={`
                        px-2 py-1 rounded-full text-xs font-medium
                        ${order.status === 'Pending' || order.status === 'pending_fulfillment' ? 'bg-yellow-500/20 text-yellow-400' : ''}
                        ${order.status === 'Shipped' || order.status === 'shipped' ? 'bg-blue-500/20 text-blue-400' : ''}
                        ${order.status === 'Delivered' || order.status === 'delivered' ? 'bg-green-500/20 text-green-400' : ''}
                      `}>
                        {order.status}
                      </span>
                    </td>
                    <td className="py-3">{order.total || (order.total_eur ? `€${order.total_eur}` : order.total_aed ? `${order.total_aed} AED` : '0')}</td>
                    <td className="py-3 text-gray-400">{order.time || (order.created_at ? new Date(order.created_at).toLocaleDateString() : '-')}</td>
                  </tr>
                ))
                ) : (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-500">No recent orders found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Alerts */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Alerts</h2>
          <div className="space-y-3">
            {Array.isArray(alerts) && alerts.length > 0 ? (
              alerts.map((alert) => (
                <div 
                  key={alert.id || alert.alert_id}
                  className="flex items-start space-x-3 p-3 rounded-lg bg-dark-900"
                >
                  {(alert.type === 'warning' || alert.severity === 'high' || alert.severity === 'medium') && (
                    <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  )}
                  {(alert.type === 'info' || alert.severity === 'low') && (
                    <div className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5 text-center">ℹ</div>
                  )}
                  {alert.type === 'success' && (
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-300">{alert.message}</p>
                    <span className="text-xs text-gray-500">{alert.market}</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="py-4 text-center text-gray-500">No active alerts.</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/products" className="btn-primary flex items-center justify-center">
            <Package className="h-5 w-5 mr-2" />
            Add Product
          </Link>
          <Link to="/goals" className="btn-primary flex items-center justify-center">
            <Target className="h-5 w-5 mr-2" />
            Set Goal
          </Link>
          <Link to="/videos" className="btn-primary flex items-center justify-center">
            <Video className="h-5 w-5 mr-2" />
            Generate Video
          </Link>
          <Link to="/analytics" className="btn-primary flex items-center justify-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            View Analytics
          </Link>
        </div>
      </div>
    </div>
  );
}
